import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { useEffect, useState, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { TimeFrameSelector, TimeFrame } from './components/TimeFrameSelector';
import { DashboardStats } from './components/DashboardStats';
import { MessagesTable } from './components/MessagesTable';
import { MessageDetailsSidebar } from './components/MessageDetailsSidebar';
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { subHours, subDays, subWeeks, subMonths } from 'date-fns';
import { AccountPage } from "@common-components/account-page/account-page";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { backendApi } from '@common-reducers/backend-api/backend-api';
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function MessagesDashboard() {
    return (
        // <AccountPage page={<MessagesDashboardContent />} />
        <MessagesDashboardContent />
    );
}

function MessagesDashboardContent() {
    // State
    const [timeFrame, setTimeFrame] = useState<TimeFrame>('1h');
    const [selectedStatus, setSelectedStatus] = useState<string | 'all'>('all');
    const [selectedMessage, setSelectedMessage] = useState<UserMessage | null>(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [searchId, setSearchId] = useState('');
    const [contactFilter, setContactFilter] = useState('');
    const [ownerFilter, setOwnerFilter] = useState('');
    const [contactType, setContactType] = useState<string>('all');
    const [showLateOnly, setShowLateOnly] = useState(true);

    // Calculate start date based on timeframe
    const startDate = useMemo(() => {
        const now = new Date();
        switch (timeFrame) {
            case '1h': return subHours(now, 1);
            case '2h': return subHours(now, 2);
            case '3h': return subHours(now, 3);
            case '6h': return subHours(now, 6);
            case '12h': return subHours(now, 12);
            case '1d': return subDays(now, 1);
            case '2d': return subDays(now, 2);
            case '3d': return subDays(now, 3);
            case '1w': return subWeeks(now, 1);
            case '1m': return subMonths(now, 1);
            default: return subHours(now, 1);
        }
    }, [timeFrame]);

    // Use the mutation hook
    const [getDashboardMessages, { data: mutationData, isLoading }] = backendApi.useGetDashboardMessagesMutation();
    const [acknowledgeMessage] = backendApi.useAcknowledgeMessageMutation();

    // Helper to check if string is a valid MongoDB ObjectId (24 hex chars)
    const isValidObjectId = (str: string) => /^[a-fA-F0-9]{24}$/.test(str);

    // Fetch messages
    const fetchMessages = () => {
        const params: any = {
            limit: 10000,
            startDate: startDate.toISOString(),
            showLateOnly
        };
        // If contactFilter looks like a MongoDB ObjectId, filter server-side
        if (contactFilter && isValidObjectId(contactFilter)) {
            params.contactId = contactFilter;
        }
        // If ownerFilter looks like a MongoDB ObjectId, filter server-side
        if (ownerFilter && isValidObjectId(ownerFilter)) {
            params.ownerId = ownerFilter;
        }
        getDashboardMessages(params);
    };

    // Fetch messages when filters change
    useEffect(() => {
        fetchMessages();

        // Optional: Polling every minute?
        const interval = setInterval(fetchMessages, 60000);
        return () => clearInterval(interval);

    }, [getDashboardMessages, startDate, showLateOnly, contactFilter, ownerFilter]);

    const handleAcknowledge = async (messageId: string) => {
        try {
            await acknowledgeMessage({ messageId }).unwrap();
            // Refetch to update list
            fetchMessages();
        } catch (error) {
            console.error('Failed to acknowledge message:', error);
        }
    };

    const messages = mutationData?.data || [];

    // Calculate stats
    const stats = useMemo(() => {
        const counts: Record<string, number> = { all: messages.length };
        messages.forEach(msg => {
            // Count by canonical status key directly (bt-sent/received/read/played/failed/...).
            const statusKey = msg.status ?? 'unknown';
            counts[statusKey] = (counts[statusKey] || 0) + 1;
            
            if (msg.isRecurring) {
                counts['recurring'] = (counts['recurring'] || 0) + 1;
            }
            if ((msg as any).deleted) {
                counts['deleted'] = (counts['deleted'] || 0) + 1;
            }
            if ((msg as any).campaignId) {
                counts['campaignId'] = (counts['campaignId'] || 0) + 1;
            }
        });
        return counts;
    }, [messages]);

    // Filter messages for table
    const filteredMessages = useMemo(() => {
        let result = messages;

        // Message ID Filter
        if (searchId) {
            result = result.filter(msg => msg._id.includes(searchId));
        }

        // Contact Text Filter (skip if it's a MongoDB ObjectId - already filtered server-side)
        if (contactFilter && !isValidObjectId(contactFilter)) {
            const lowerFilter = contactFilter.toLowerCase();
            result = result.filter(msg => {
                const mobile = msg.contactList?.[0]?.mobile || '';
                const id = msg.contactList?.[0]?.id || '';
                const name = msg.contactName || '';
                return mobile.toLowerCase().includes(lowerFilter) ||
                       id.toLowerCase().includes(lowerFilter) ||
                       name.toLowerCase().includes(lowerFilter);
            });
        }

        // Contact Type Filter
        if (contactType !== 'all') {
            result = result.filter(msg => {
                const id = msg.contactList?.[0]?.id || '';
                if (contactType === 'c.us') return id.endsWith('@c.us');
                if (contactType === 'g.us') return chatKindOf(id) === 'group';
                if (contactType === 'lid') return id.includes('lid');
                if (contactType === 'newsletter') return chatKindOf(id) === 'channel';
                return true;
            });
        }

        if (selectedStatus === 'all') {
            // By default, filter out 'confirmed' messages as they are less interesting/noisy
            return result.filter(msg => msg.status !== 'confirmed');
        }
        if (selectedStatus === 'recurring') return result.filter(msg => msg.isRecurring);
        if (selectedStatus === 'deleted') return result.filter(msg => msg.deleted);
        if (selectedStatus === 'campaignId') return result.filter(msg => msg.campaignId);
        
        // 'error' is a legacy filter alias for 'failed' — UserMessage.status
        // never holds 'error' anymore (the dead-code comparison was removed
        // when the bt-common enum became strict).
        if (selectedStatus === 'error') {
            return result.filter(msg => msg.status === 'failed');
        }
        
        return result.filter(msg => msg.status === selectedStatus);
    }, [messages, selectedStatus, searchId, contactFilter, contactType]);

    // Handlers
    const handleMessageClick = (message: UserMessage) => {
        setSelectedMessage(message);
        setSidebarOpen(true);
    };

    return (
        <div className="p-6 space-y-6 h-full flex flex-col overflow-hidden">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">Messages Dashboard</h1>
                {isLoading && <Loader2 className="animate-spin text-muted-foreground" />}
            </div>

            <div className="flex gap-4 items-end flex-wrap">
                <TimeFrameSelector value={timeFrame} onValueChange={setTimeFrame} />
                
                <div className="w-[250px]">
                    <label className="text-sm font-medium mb-1.5 block">Message ID</label>
                     <Input 
                        placeholder="Search ID..." 
                        value={searchId}
                        onChange={(e) => setSearchId(e.target.value)}
                    />
                </div>

                <div className="w-[250px]">
                    <label className="text-sm font-medium mb-1.5 block">Contact</label>
                     <Input 
                        placeholder="Name, Mobile, ID..." 
                        value={contactFilter}
                        onChange={(e) => setContactFilter(e.target.value)}
                    />
                </div>

                <div className="w-[180px]">
                    <label className="text-sm font-medium mb-1.5 block">Contact Type</label>
                    <Select value={contactType} onValueChange={setContactType}>
                        <SelectTrigger>
                            <SelectValue placeholder="All Types" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            <SelectItem value="c.us">User (c.us)</SelectItem>
                            <SelectItem value="g.us">Group (g.us)</SelectItem>
                            <SelectItem value="lid">LID</SelectItem>
                            <SelectItem value="newsletter">Newsletter</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="w-[250px]">
                    <label className="text-sm font-medium mb-1.5 block">Owner</label>
                    <Input
                        placeholder="Owner ID..."
                        value={ownerFilter}
                        onChange={(e) => setOwnerFilter(e.target.value)}
                    />
                </div>

                <div className="flex items-center space-x-2 pb-2">
                    <Switch id="show-late" checked={showLateOnly} onCheckedChange={setShowLateOnly} />
                    <Label htmlFor="show-late">Show Late/Stuck Only</Label>
                </div>
            </div>

            <DashboardStats 
                stats={stats} 
                selectedStatus={selectedStatus} 
                onStatusClick={setSelectedStatus} 
            />

            <div className="flex-1 overflow-auto border rounded-md bg-white dark:bg-slate-950 p-1">
                <MessagesTable 
                    messages={filteredMessages} 
                    onMessageClick={handleMessageClick} 
                    onAcknowledge={handleAcknowledge}
                />
            </div>

            <MessageDetailsSidebar 
                open={sidebarOpen} 
                onOpenChange={setSidebarOpen} 
                message={selectedMessage} 
            />
        </div>
    );
}
